import { useState } from "react";
import { apiClient } from "@/lib/api";
import { BellRing, CheckCircle2, Loader2 } from "lucide-react";

/**
 * Lead capture form displayed on prospected city pages.
 *
 * Props:
 * - citySlug, cityLabel (required)
 * - context: "city" | "metier" | "travaux" | "persona"
 * - metierSlug / travauxSlug / personaSlug (optional)
 * - defaultRole: "demandeur" (default)
 */
export default function CityProspectForm({
  citySlug,
  cityLabel,
  context = "city",
  metierSlug = null,
  travauxSlug = null,
  personaSlug = null,
  defaultRole = "demandeur",
}) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState(defaultRole);
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [message, setMessage] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      setStatus("error");
      setMessage("Merci d'entrer un email valide.");
      return;
    }
    setStatus("submitting");
    try {
      const { data } = await apiClient.post("/public/city-prospect", {
        email: email.trim(),
        city_slug: citySlug,
        city_label: cityLabel,
        context,
        metier_slug: metierSlug,
        travaux_slug: travauxSlug,
        persona_slug: personaSlug,
        role,
      });
      setStatus("success");
      setMessage(data?.message || `Merci ! Vous serez notifié dès qu'un artisan sera disponible à ${cityLabel}.`);
    } catch (err) {
      setStatus("error");
      setMessage("Une erreur est survenue. Merci de réessayer.");
    }
  };

  if (status === "success") {
    return (
      <div
        className="border-2 border-[#0A0A0B] bg-[#FF4D00] text-white p-6 shadow-[4px_4px_0_#0A0A0B]"
        data-testid={`city-prospect-success-${citySlug}`}
      >
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-6 h-6 shrink-0" strokeWidth={2.5} />
          <div>
            <div className="display text-xl leading-tight">Merci, on garde le contact !</div>
            <p className="text-sm mt-2 text-white/90">{message}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="border-2 border-[#0A0A0B] bg-white p-6 shadow-[4px_4px_0_#FF4D00]"
      data-testid={`city-prospect-form-${citySlug}`}
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 border-2 border-[#0A0A0B] bg-[#FF4D00] flex items-center justify-center shrink-0">
          <BellRing className="w-5 h-5 text-white" strokeWidth={2.5} />
        </div>
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-[#FF4D00] font-semibold">// Alerte gratuite</div>
          <div className="display text-xl leading-tight mt-1">
            Notifiez-moi quand un artisan sera disponible à {cityLabel}
          </div>
          <p className="text-sm text-zinc-600 mt-1">
            {role === "artisan"
              ? `Vous êtes artisan à ${cityLabel} ? Réservez votre place dès maintenant.`
              : `Recevez un email dès qu'un artisan SIRET vérifié s'inscrit à ${cityLabel}. Aucun spam, désinscription en 1 clic.`}
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="votre@email.fr"
          className="flex-1 border-2 border-[#0A0A0B] bg-white px-4 py-3 mono text-sm focus:outline-none focus:bg-[#FFF7ED]"
          data-testid={`city-prospect-email-${citySlug}`}
          disabled={status === "submitting"}
        />
        <button
          type="submit"
          className="btn-batisur whitespace-nowrap flex items-center justify-center gap-2"
          data-testid={`city-prospect-submit-${citySlug}`}
          disabled={status === "submitting"}
        >
          {status === "submitting" ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {status === "submitting" ? "Envoi..." : "Me notifier →"}
        </button>
      </div>

      <div className="mt-3 flex items-center gap-4 text-xs">
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input
            type="radio"
            name={`role-${citySlug}`}
            value="demandeur"
            checked={role === "demandeur"}
            onChange={() => setRole("demandeur")}
            data-testid={`city-prospect-role-demandeur-${citySlug}`}
            className="accent-[#FF4D00]"
          />
          <span>Je cherche un artisan</span>
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input
            type="radio"
            name={`role-${citySlug}`}
            value="artisan"
            checked={role === "artisan"}
            onChange={() => setRole("artisan")}
            data-testid={`city-prospect-role-artisan-${citySlug}`}
            className="accent-[#FF4D00]"
          />
          <span>Je suis artisan à {cityLabel}</span>
        </label>
      </div>

      {status === "error" && (
        <p className="mt-3 text-sm text-red-600" data-testid={`city-prospect-error-${citySlug}`}>
          {message}
        </p>
      )}
    </form>
  );
}
